import { useCallback, useRef, useState } from 'react'

import {
  generateMusic,
  getTaskStatus,
  prepareAdvancedVideoEditor,
  getAdvancedVideoEditorJobStatus,
  getAdvancedVideoEditorSession,
  getAdvancedVideoEditorExports
} from '../../../services/api/aiVideoGeneration'
import { getTimelineEditorUrl } from '../utils/timelineEditor'

const POLL_INTERVAL = 2500
const POLL_TIMEOUT = 20 * 60 * 1000
const SUCCESS_STATUSES = ['success', 'completed', 'done', 'ready']
const FAILURE_STATUSES = ['failed', 'error', 'cancelled']

const wait = duration =>
  new Promise(resolve => {
    setTimeout(resolve, duration)
  })

const getResponseData = response =>
  response?.data?.data || response?.data || response || {}

const normalizeProgress = value => {
  if (value === null || value === undefined || value === '') return null

  const numericValue =
    typeof value === 'string' ? Number(value.replace('%', '')) : Number(value)

  if (!Number.isFinite(numericValue)) return null
  return numericValue <= 1
    ? Math.round(numericValue * 100)
    : Math.round(numericValue)
}

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback

const pollTask = async ({
  getStatus,
  isCurrent,
  onProgress,
  timeout = POLL_TIMEOUT
}) => {
  const startedAt = Date.now()

  while (Date.now() - startedAt < timeout) {
    if (!isCurrent()) {
      throw new Error('Timeline editor preparation was cancelled')
    }

    const response = await getStatus()
    const data = getResponseData(response)
    const status = String(data.status || '').toLowerCase()
    const progress = normalizeProgress(data.progress)

    if (progress !== null) {
      onProgress(progress)
    }

    if (
      SUCCESS_STATUSES.includes(status) ||
      data.ready === true ||
      progress >= 100
    ) {
      return data
    }

    if (FAILURE_STATUSES.includes(status)) {
      throw new Error(
        data.error_msg ||
          data.error ||
          data.message ||
          'Timeline editor preparation failed'
      )
    }

    await wait(POLL_INTERVAL)
  }

  throw new Error('Timeline editor preparation timed out')
}

const useTimelineEditorFlow = ({ buildPreparePayload, onProjectCreated }) => {
  const operationIdRef = useRef(0)
  const exportProjectIdRef = useRef(null)

  const [setupVisible, setSetupVisible] = useState(false)
  const [setupContext, setSetupContext] = useState(null)
  const [setupSubmitting, setSetupSubmitting] = useState(false)
  const [setupLoading, setSetupLoading] = useState(false)

  const [editorVisible, setEditorVisible] = useState(false)
  const [editorUrl, setEditorUrl] = useState(null)
  const [editorLoading, setEditorLoading] = useState(false)
  const [editorError, setEditorError] = useState(null)
  const [editorStatus, setEditorStatus] = useState('')
  const [editorProgress, setEditorProgress] = useState(0)

  const [exportsVisible, setExportsVisible] = useState(false)
  const [exportsLoading, setExportsLoading] = useState(false)
  const [exportsError, setExportsError] = useState(null)
  const [exports, setExports] = useState([])

  const beginOperation = useCallback(() => {
    operationIdRef.current += 1
    return operationIdRef.current
  }, [])

  const isOperationCurrent = useCallback(
    operationId => operationIdRef.current === operationId,
    []
  )

  const openExistingProject = useCallback(
    async context => {
      const operationId = beginOperation()

      setEditorVisible(true)
      setEditorUrl(null)
      setEditorError(null)
      setEditorLoading(true)
      setEditorProgress(20)
      setEditorStatus('Restoring timeline session...')

      try {
        const response = await getAdvancedVideoEditorSession(context.projectId)
        const session = getResponseData(response)

        if (!isOperationCurrent(operationId)) return

        const resolvedEditorUrl =
          session.editor_url || getTimelineEditorUrl(context.projectId)

        if (!resolvedEditorUrl) {
          throw new Error('Timeline session did not include an editor URL')
        }

        setEditorProgress(100)
        setEditorStatus('Timeline ready')
        setEditorUrl(resolvedEditorUrl)
      } catch (error) {
        if (!isOperationCurrent(operationId)) return
        setEditorError(
          getErrorMessage(error, 'Failed to restore the timeline editor')
        )
      } finally {
        if (isOperationCurrent(operationId)) {
          setEditorLoading(false)
        }
      }
    },
    [beginOperation, isOperationCurrent]
  )

  const openEditor = useCallback(
    context => {
      if (context?.projectId) {
        openExistingProject(context)
        return
      }

      setSetupContext(context)
      setSetupVisible(true)
    },
    [openExistingProject]
  )

  const startNewProject = useCallback(
    async ({ includeMusic = false, userPrompt = '', musicVolume = 0.3 }) => {
      if (!setupContext) return

      const operationId = beginOperation()
      const isCurrent = () => isOperationCurrent(operationId)

      setSetupLoading(false)
      setSetupSubmitting(true)
      setSetupVisible(false)
      setEditorVisible(true)
      setEditorUrl(null)
      setEditorError(null)
      setEditorLoading(true)
      setEditorProgress(5)
      setEditorStatus('Building timeline payload...')

      try {
        const preparedResult = await buildPreparePayload(setupContext)
        const payload = preparedResult?.payload || preparedResult
        const sourceData = preparedResult?.sourceData || null

        if (!payload?.clustered_shooting_list) {
          throw new Error('Timeline payload is missing the shooting list')
        }

        let musicUrl = null

        if (includeMusic) {
          setEditorStatus('Generating background music...')
          setEditorProgress(10)

          const musicResponse = await generateMusic({
            mode: 'shootinglist',
            shooting_list: payload.clustered_shooting_list,
            user_prompt: userPrompt,
            music_volume: musicVolume,
            video_id: setupContext.videoId
          })
          const musicTaskId = getResponseData(musicResponse).task_id

          if (!musicTaskId) {
            throw new Error('Music generation did not return a task ID')
          }

          const musicResult = await pollTask({
            getStatus: () => getTaskStatus(musicTaskId),
            isCurrent,
            onProgress: progress => {
              setEditorProgress(10 + Math.round(progress * 0.35))
            }
          })

          musicUrl = musicResult?.output?.full_music_s3_url

          if (!musicUrl) {
            throw new Error('Music generation completed without an audio URL')
          }
        }

        if (!isCurrent()) return

        setEditorStatus('Preparing editable timeline...')
        setEditorProgress(includeMusic ? 50 : 20)

        const prepareResponse = await prepareAdvancedVideoEditor({
          ...payload,
          name: setupContext.projectName || 'AI Video Timeline',
          redirect_url:
            setupContext.redirectUrl ||
            (typeof window !== 'undefined' ? window.location.href : ''),
          ...(musicUrl
            ? {
                music_url: musicUrl,
                music_volume: musicVolume
              }
            : {})
        })
        const prepareData = getResponseData(prepareResponse)

        if (!prepareData.project_id) {
          throw new Error('Timeline preparation did not return a project ID')
        }

        if (onProjectCreated) {
          await onProjectCreated({
            projectId: prepareData.project_id,
            context: setupContext,
            sourceData,
            response: prepareData
          })
        }

        if (!isCurrent()) return

        if (
          !prepareData.ready &&
          (prepareData.status_url || prepareData.job_id)
        ) {
          setEditorStatus('Uploading timeline media...')
          setEditorProgress(includeMusic ? 55 : 30)

          await pollTask({
            getStatus: () =>
              getAdvancedVideoEditorJobStatus(
                prepareData.status_url || prepareData.job_id
              ),
            isCurrent,
            onProgress: progress => {
              const baseProgress = includeMusic ? 55 : 30
              const remainingProgress = 100 - baseProgress
              setEditorProgress(
                baseProgress + Math.round((progress / 100) * remainingProgress)
              )
            }
          })
        }

        if (!isCurrent()) return

        setEditorStatus('Timeline ready')
        setEditorProgress(100)
        setEditorUrl(
          prepareData.editor_url || getTimelineEditorUrl(prepareData.project_id)
        )
      } catch (error) {
        if (!isCurrent()) return
        setEditorError(
          getErrorMessage(error, 'Failed to prepare the timeline editor')
        )
      } finally {
        if (isCurrent()) {
          setSetupSubmitting(false)
          setEditorLoading(false)
        }
      }
    },
    [
      beginOperation,
      buildPreparePayload,
      isOperationCurrent,
      onProjectCreated,
      setupContext
    ]
  )

  const closeSetup = useCallback(() => {
    setSetupVisible(false)
    setSetupContext(null)
    setSetupLoading(false)
  }, [])

  const closeEditor = useCallback(() => {
    beginOperation()
    setEditorVisible(false)
    setEditorLoading(false)
    setEditorUrl(null)
    setEditorError(null)
    setEditorStatus('')
    setEditorProgress(0)
  }, [beginOperation])

  const loadExports = useCallback(async projectId => {
    if (!projectId) return

    exportProjectIdRef.current = projectId
    setExportsLoading(true)
    setExportsError(null)

    try {
      const response = await getAdvancedVideoEditorExports(projectId)
      const data = getResponseData(response)
      setExports(Array.isArray(data.exports) ? data.exports : [])
    } catch (error) {
      setExportsError(getErrorMessage(error, 'Failed to load timeline exports'))
    } finally {
      setExportsLoading(false)
    }
  }, [])

  const openExports = useCallback(
    projectId => {
      setExportsVisible(true)
      setExports([])
      loadExports(projectId)
    },
    [loadExports]
  )

  const closeExports = useCallback(() => {
    setExportsVisible(false)
    setExportsError(null)
  }, [])

  const refreshExports = useCallback(() => {
    loadExports(exportProjectIdRef.current)
  }, [loadExports])

  return {
    openEditor,
    openExports,
    setSetupLoading,
    setupModalProps: {
      visible: setupVisible,
      submitting: setupSubmitting,
      loading: setupLoading,
      onCancel: closeSetup,
      onSubmit: startNewProject
    },
    editorModalProps: {
      visible: editorVisible,
      editorUrl,
      loading: editorLoading,
      error: editorError,
      statusText: editorStatus,
      progress: editorProgress,
      onClose: closeEditor
    },
    exportsModalProps: {
      visible: exportsVisible,
      loading: exportsLoading,
      error: exportsError,
      exports,
      onClose: closeExports,
      onRefresh: refreshExports
    }
  }
}

export default useTimelineEditorFlow
